import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { StateCleanupService } from './state-cleanup.service';

export interface DozentSocketMessage {
  event: string;
  data: any;
  event_id?: string;
}

const REALTIME_LAST_EVENT_ID_PREFIX = 'al-dozent-lastRealtimeEventId';

@Injectable({
  providedIn: 'root',
})
export class DozentSocketService implements OnDestroy {
  private ws: WebSocket | null = null;
  private pingInterval: any = null;
  private reconnectTimeout: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;

  isConnected$ = new BehaviorSubject<boolean>(false);
  newMessage$ = new Subject<any>();
  // Generic notification stream. ALL dozent-side notifications flow
  // through this — `payload.type` carries the kind (`ai_escalation`,
  // `submission_review`, `learning_suggestion`, …). Subscribers
  // route by type. Do NOT add new `SC_*` events for every
  // notification kind — pick a `type` string and ship it through
  // the existing `SC_DOZENT_NOTIFICATION` channel.
  notification$ = new Subject<any>();
  presenceUpdate$ = new Subject<any>();
  event$ = new Subject<DozentSocketMessage>();

  constructor(
    private authService: AuthService,
    private stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.disconnect());

    // Auto-connect on auth-state. As soon as a valid access-token is
    // present we open the dozent WS — independent of which page is
    // currently rendered. This used to only fire in
    // `dashboard-header.ngOnInit`, which meant any page that didn't
    // mount that component (auth pages, deep-linked routes during
    // initial load, etc.) silently dropped real-time pushes until
    // the user navigated back to a page that did. Listening to
    // `accessTokenPayload$` makes the WS connection follow the
    // auth lifecycle directly: connect on login, disconnect on
    // logout / token clear.
    this.authService.accessTokenPayload$.subscribe((payload) => {
      if (payload) {
        // Best-effort connect; the existing `connect()` is a no-op
        // when already OPEN, and the reconnect-loop handles
        // transient failures.
        this.connect();
      } else {
        this.disconnect();
      }
    });
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    const wsUrl = environment.tutorsBackend
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');

    this.ws = new WebSocket(`${wsUrl}/ws/al-dozent`);

    this.ws.onopen = () => {
      // eslint-disable-next-line no-console
      console.log('[dozent-ws] OPEN — sending init');
      this.isConnected$.next(true);
      this.reconnectAttempts = 0;
      this.sendInit();
      this.startPing();
    };

    this.ws.onmessage = (event) => {
      try {
        const msg: DozentSocketMessage = JSON.parse(event.data);
        this.handleEvent(msg);
      } catch {}
    };

    this.ws.onclose = (ev) => {
      // eslint-disable-next-line no-console
      console.log('[dozent-ws] CLOSE', ev.code, ev.reason);
      this.isConnected$.next(false);
      this.stopPing();
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // eslint-disable-next-line no-console
      console.warn('[dozent-ws] ERROR');
      this.isConnected$.next(false);
    };
  }

  disconnect() {
    this.stopPing();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected$.next(false);
  }

  private sendInit() {
    try {
      const stored = localStorage.getItem('auth_app_token');
      if (stored) {
        const parsed = JSON.parse(stored);
        const token = parsed?.value?.access_token ?? parsed?.access_token;
        if (token) {
          this.send('CS_DOZENT_INIT', {
            token,
            lastRealtimeEventId: this.getLastRealtimeEventId(),
          });
        }
      }
    } catch {}
  }

  send(event: string, data: any = {}) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ event, data }));
    }
  }

  sendMessage(conversationId: string, message: string) {
    this.send('CS_DOZENT_SEND_MESSAGE', { conversation_id: conversationId, message });
  }

  markRead(conversationId: string) {
    this.send('CS_DOZENT_MARK_READ', { conversation_id: conversationId });
  }

  private handleEvent(msg: DozentSocketMessage) {
    // Diagnostic — visible in browser DevTools to confirm whether
    // real-time pushes are arriving. Cheap; left in place because
    // the bell + messages-page hot-load paths all hinge on this
    // pipe being healthy.
    console.log('[DozentSocket] in:', msg.event, msg.data);

    if (this.shouldSkipRealtimeMessage(msg)) {
      this.ackRealtimeEvent(msg.event_id);
      return;
    }

    this.storeRealtimeEventId(msg.event_id);
    this.ackRealtimeEvent(msg.event_id);

    this.event$.next(msg);

    switch (msg.event) {
      case 'SC_DOZENT_NEW_MESSAGE':
        // Legacy event — kept for backwards compat with older
        // backend deployments. New backends ship the same payload
        // through `SC_DOZENT_NOTIFICATION{type:'new_message'}` (see
        // case below) and we re-emit on `newMessage$` from there.
        this.newMessage$.next(msg.data);
        break;
      case 'SC_DOZENT_NOTIFICATION':
        // Diagnostic log so the operator can verify in DevTools that
        // live pushes actually arrive. If you see "[dozent-ws]
        // notification arrived" appear in the console the moment a
        // new event happens, the WS path is healthy and any "only
        // after refresh" symptom is a downstream rendering issue.
        // If this log NEVER appears, the backend either didn't push
        // or the user wasn't in the recipient set.
        // eslint-disable-next-line no-console
        console.log('[dozent-ws] notification arrived', msg.data);
        this.notification$.next(msg.data);
        // Type-routing: when the notification IS a chat-thread
        // payload (`type='new_message'`), re-emit on `newMessage$`
        // with the metadata so the messages page can append the
        // bubble. Single-channel-per-role on the wire, two-stream
        // routing on the consumer side.
        if (msg.data?.type === 'new_message' && msg.data?.metadata) {
          this.newMessage$.next(msg.data.metadata);
        }
        break;
      case 'SC_DOZENT_PRESENCE_UPDATE':
        this.presenceUpdate$.next(msg.data);
        break;
    }
  }

  private startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      this.send('CS_DOZENT_PING');
    }, 10000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    this.reconnectTimeout = setTimeout(() => this.connect(), delay);
  }

  private realtimeStorageKey(): string {
    const userId = this.authService.accessTokenPayload$.value?.user_id || 'anonymous';
    return `${REALTIME_LAST_EVENT_ID_PREFIX}:${userId}`;
  }

  private getLastRealtimeEventId(): string | undefined {
    return localStorage.getItem(this.realtimeStorageKey()) || undefined;
  }

  private storeRealtimeEventId(eventId?: string) {
    if (!eventId) return;
    const last = this.getLastRealtimeEventId();
    if (!last || this.compareRedisStreamIds(eventId, last) > 0) {
      localStorage.setItem(this.realtimeStorageKey(), eventId);
    }
  }

  private shouldSkipRealtimeMessage(msg: DozentSocketMessage): boolean {
    if (!msg.event_id) return false;
    const last = this.getLastRealtimeEventId();
    return !!last && this.compareRedisStreamIds(msg.event_id, last) <= 0;
  }

  private ackRealtimeEvent(eventId?: string) {
    if (!eventId || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      event: 'CS_DOZENT_ACK',
      data: { event_id: eventId },
    }));
  }

  private compareRedisStreamIds(a: string, b: string): number {
    const [amsRaw, aseqRaw] = String(a).split('-');
    const [bmsRaw, bseqRaw] = String(b).split('-');
    const ams = Number(amsRaw);
    const bms = Number(bmsRaw);
    if (Number.isFinite(ams) && Number.isFinite(bms) && ams !== bms) {
      return ams - bms;
    }
    const aseq = Number(aseqRaw);
    const bseq = Number(bseqRaw);
    if (Number.isFinite(aseq) && Number.isFinite(bseq)) {
      return aseq - bseq;
    }
    return String(a).localeCompare(String(b));
  }

  ngOnDestroy() {
    this.disconnect();
  }
}
